/* eslint-disable no-nested-ternary */
import React, { Component, type Node, type Element } from 'react';
import PropTypes from 'prop-types';
import {
  defineMessages,
  FormattedMessage,
  FormattedNumber,
  injectIntl,
} from 'react-intl';
import { forceCheck } from 'react-lazyload';
import { findKey, pick } from 'lodash';
import { createPortal } from 'react-dom';
import Button from 'brastrap/common/button/Button';
import Link from 'brastrap/containers/link/LinkContainer';
import Accordion from 'brastrap/common/accordion/Accordion';
import PromotionMessage from 'brastrap/promotional/promotion-message/PromotionMessage';
import BagItem from './BagItem';
import BagItemMessage from './BagItemMessage';
import { applyModifiers } from '../../utils';
import IconInformation from '../../common/icon/variants/IconInformation';
import { dataLayerFCOEditButtonClick } from '../../../../shared/utils/data-layer';

const STYLE_PREFIX = 'c-bag';

const messages = defineMessages({
  summary: { id: 'bag.table.summary', defaultMessage: 'Items in bag' },
  editButton: { id: 'bag.edit-button', defaultMessage: 'Edit bag' },
  includingDelivery: {
    id: 'bag.including-delivery',
    defaultMessage: ' (incl. delivery & tax)',
  },
});

/**
 * @constant
 * @type {{warehouse: number, shop: number, unknown: number}}
 */
export const LOCATION_STOCK_GROUP_MAP = {
  warehouse: 1,
  shop: 2,
  unknown: 3,
};

/**
 * @param {String} location
 * @param {Array} items
 * @returns {Boolean}
 */
const hasItemsLocatedIn = (location = 'shop', items) =>
  !!(
    items[LOCATION_STOCK_GROUP_MAP[location]] &&
    items[LOCATION_STOCK_GROUP_MAP[location]].length
  );

/**
 * @param {String} location
 * @param {Array} items
 * @param {Object} titleMessages
 * @param {Boolean} upSellPremiumPostage
 * @param {Function} onUpSellPostage
 * @returns {Array}
 */
const getItemsLocatedIn = (
  location: LocationType = 'shop',
  items: Element<*>[][],
  titleMessages: TitleMessages,
  upSellPremiumPostage: boolean = false,
  onUpSellPostage: () => void = () => {}
): Array<?Node> => {
  const bagItems = items[LOCATION_STOCK_GROUP_MAP[location]];
  const titleMessage = titleMessages && titleMessages[location];

  return hasItemsLocatedIn(location, items)
    ? [
        <BagItemMessage key={location}>
          {
            <div className="c-bag__located-in-message-container">
              <IconInformation stroke="#4A90E2" />
              <p className="c-bag__located-in-message">{titleMessage}</p>
            </div>
          }
        </BagItemMessage>,

        // If they are available for next day delivery, then add the upgrade postage text.
        upSellPremiumPostage && (
          <BagItemMessage key="upgrade-postage" modifiers={['upgrade']}>
            <Button
              modifiers={['small', 'upgrade-postage']}
              label="Upgrade"
              onClick={onUpSellPostage}
            />
            <FormattedMessage
              id="bag.premium-delivery.upsell"
              defaultMessage="Need them sooner?"
            />
          </BagItemMessage>
        ),

        ...bagItems,
      ]
    : [];
};

/**
 * @param {Object} props
 * @return {XML}
 * @constructor
 */
class Bag extends Component<Props> {
  footer: ?Node;

  static defaultProps = {
    displaySaveItemButton: false,
    displaySignInLink: false,
    delivery: {
      methodLabel: null,
      postage: null,
      discount: 0,
    },
    quantity: {
      options: [],
    },
    isEditable: false,
    items: [],
    payment: {
      giftVouchers: [],
    },
    onChange: () => {},
    onDelete: () => {},
    onUpSellPostage: () => {},
    premiumUpgradeId: 'next',
    promotionMessages: [],
    showPostage: false,
    currency: 'GBP',
    disableClickableThumbnails: false,
    onFooterRender: () => {},
    children: null,
    checkoutStep: 1,
  };

  state = {
    hasAccordionBeenOpened: false,
  };

  /** *
   * Calls onFooterRender on first render
   * ** */
  componentDidMount() {
    this.callOnFooterRender();
  }

  /** *
   * Calls onFooterRender on component update
   * @param {object} prevProps
   * ** */
  componentDidUpdate(prevProps) {
    if (this.shouldUpdateFooter(prevProps)) {
      this.callOnFooterRender();
    }
  }

  /** *
   * Logic to detrmine if it needs HOF call depending on if total, total items or quantity of items change
   * @param {object} prevProps
   * @return {boolean}
   * ** */
  shouldUpdateFooter(prevProps) {
    const { items, total } = this.props;
    return (
      prevProps.total !== total ||
      prevProps.items.length !== items.length ||
      prevProps.items.some(
        (item, i) =>
          item.code !== items[i].code || item.quantity !== items[i].quantity
      )
    );
  }

  /** *
   * Calls onFooterRender based on component location
   * ** */
  callOnFooterRender() {
    const { location, onFooterRender } = this.props;
    if (location === 'bagPage' && onFooterRender) {
      onFooterRender(this.footer);
    }

    this.setDynamicBagHeight();
  }

  heightBagSummary = null;
  static Item = BagItem;
  static ItemMessage = BagItemMessage;

  /** *
   *  Sets height of bag on bag page etc according to available space
   */
  setDynamicBagHeight() {
    const navSection = document.querySelector(
      '.c-masthead__navigation-section'
    );
    const bagFooter = document.querySelector('.c-bag__footer');

    const heightNavSection = navSection ? navSection.offsetHeight : 0;
    const heightBagFooter = bagFooter ? bagFooter.offsetHeight : 0;

    this.heightBagSummary =
      window.innerHeight - (heightNavSection + heightBagFooter);
  }

  /** *
   * Renders the mobile bag in the bandeau
   * @param {object} summaryContent
   * @return {object}
   */
  renderMobileSummary(summaryContent) {
    const anchor = document.getElementById('bandeau-portal-anchor');

    if (this.props.isMobile && anchor) {
      return createPortal(summaryContent, anchor);
    }

    return summaryContent;
  }

  /**
   * @return {*}
   */
  render() {
    if (!(this.props.items && this.props.items.length > 0)) {
      return (
        <div id="bag-empty" style={{ textAlign: 'center', marginTop: '3rem' }}>
          <FormattedMessage
            id="bag.empty"
            defaultMessage="You have no items in your bag"
          />
          <br />
          {this.props.displaySignInLink && (
            <FormattedMessage
              id="bag.empty.sign-in"
              defaultMessage="{signIn} to view your saved items."
              values={{
                signIn: (
                  <Link href="/login">
                    <FormattedMessage
                      id="link.sign-in"
                      defaultMessage="Sign in"
                    />
                  </Link>
                ),
              }}
            />
          )}
        </div>
      );
    }

    const {
      delivery: { postage, discount = 0 },
      currency,
      intl: { formatMessage },
      isDomestic,
      isEditable,
      isPremiumAvailable,
      premium,
      items,
      titleMessages,
      onUpSellPostage,
      payment: { giftVouchers = [] },
      promotionMessages,
      showPostage,
      splitItemsByDeliveryTimes,
      subTotal,
      total,
      usTaxAmount,
      locale,
      showUSTax,
      isPage,
      location,
      children,
      showEditButton,
      title,
      isMobile,
      checkoutStep,
    } = this.props;

    // TODO: Refactor this logic (reused in several places) - UST timeline hasn't allowed time.
    // GVs make US tax hard!
    // TL;DR - the way that GVs work is to reduce the order.total by the value of
    // the GV. However, the order.total does not include tax (as defined by the
    // API contract and orderXml SP). This doesn't work for US orders that attract
    // sales tax, as the order and delivery get paid for by GV, but the tax does not!
    // This logic below means we calculate a special `totalUsAmount` to ensure
    // usTax is included in the total, even when GVs are used!
    let totalUSAmount;
    if (this.props.payment.giftVouchers) {
      const itemTotals = this.props.items.reduce(
        (itemTotal, item) => itemTotal + item.price * item.quantity,
        0
      );
      const postagePrice = this.props.delivery.postage || 0;
      const postageDiscount = this.props.delivery.discount || 0;
      const postageTotal = postagePrice - postageDiscount;

      const gvTotal = this.props.payment.giftVouchers.reduce(
        (giftVoucherTotal, gv) => giftVoucherTotal + gv.value,
        0
      );

      totalUSAmount = Math.max(
        itemTotals + postageTotal + usTaxAmount - gvTotal,
        0
      );
    } else {
      totalUSAmount = total + usTaxAmount;
    }

    const cols = isEditable ? 4 : 3;
    const upSellPremiumPostage = isPremiumAvailable && !premium;

    const bagItemProps = pick(
      this.props,
      'currency',
      'isEditable',
      'onChange',
      'onDelete',
      'disableClickableThumbnails',
      'displaySaveItemButton',
      'onSaveItemPress',
      'savedItemsMap'
    );

    const isCheckoutFlow = title && showEditButton;

    const deliveryNotification = isMarginless => (
      <div
        className={`c-bag__delivery-notification ${
          isMarginless && 'c-bag__delivery-notification--marginless'
        }`}
      >
        <IconInformation stroke="white" fill="#616365" />
        <FormattedMessage
          id="bag.delivery-notification"
          defaultMessage="Some of the items in your order may take a little longer to arrive. (Please see order summary)"
        />
      </div>
    );

    this.footer = (
      <tfoot
        className={`c-bag__footer ${
          isCheckoutFlow ? 'c-bag__footer--border' : ''
        }`}
      >
        {
          // TODO: add in discounts etc
          // <tr className="c-bag__total c-bag__total--discount">
          //   <td className="c-bag__total__value" colSpan="4">10% partner promotional discount:
          //     <span className="c-bag__total__price c-bag__price"> -£7.15</span>
          //   </td>
          // </tr>
        }

        {title && (
          <tr>
            <td colSpan={cols}>
              <div className="c-bag__title-container">
                <div className="c-bag__title">{title}</div>
                {showEditButton && (
                  <Link
                    className="c-bag__edit-button"
                    href="/bag"
                    rel="bag"
                    onClick={() => dataLayerFCOEditButtonClick()}
                  >
                    {formatMessage(messages.editButton)}
                  </Link>
                )}
              </div>
            </td>
          </tr>
        )}

        {!isMobile &&
          isCheckoutFlow &&
          items.length > 3 &&
          items.findIndex(item => item.stockStatus !== 'InStock') >= 3 && (
            <tr>
              <td colSpan={cols}>{deliveryNotification(false)}</td>
            </tr>
          )}

        {giftVouchers.length > 0 && (
          <tr className="c-bag__total">
            <td className="c-bag__total__value" colSpan={cols}>
              <FormattedMessage
                id="bag.gift-voucher-applied"
                defaultMessage="Gift Vouchers Applied: "
              />
              <span className="c-bag__total__price c-bag__price">&nbsp;</span>
            </td>
          </tr>
        )}
        {giftVouchers.map((giftVoucher, index) => {
          const value = 0 - giftVoucher.value;
          const gv = giftVoucher.reference || '';
          const giftVoucherReference = `${gv.substring(0, 4)}-${gv.substring(
            4,
            8
          )}-${gv.substring(8, 12)}-${gv.substring(12, 16)}`;
          return (
            <tr
              className="c-bag__total c-bag__total--gift-voucher"
              key={index}
              tabIndex="0"
              aria-label={`Gift voucher value applied: ${currency}${value}`}
            >
              <td className="c-bag__total__value" colSpan={cols}>
                <div className="c-bag__price-container">
                  <span
                    id={`${giftVoucherReference.replace(
                      /-/g,
                      ''
                    )}-bag-gift-voucher`}
                    className="gift-voucher-reference"
                  >
                    {giftVoucherReference}
                  </span>
                  <strong className="c-bag__total__price c-bag__price">
                    <FormattedNumber
                      value={value}
                      currency={currency}
                      style="currency"
                    />
                  </strong>
                </div>
              </td>
            </tr>
          );
        })}
        <tr className="c-bag__total c-bag__sub__total">
          <td
            className="c-bag__total__value"
            colSpan={cols}
            tabIndex="0"
            aria-label={`Total excluding delivery ${subTotal}${currency}`}
          >
            <div className="c-bag__price-container">
              <FormattedMessage
                id="bag.total"
                defaultMessage="Total (excluding delivery):"
              />
              <strong className="c-bag__total__price c-bag__price">
                <FormattedNumber
                  value={subTotal}
                  currency={currency}
                  style="currency"
                />
              </strong>
            </div>
          </td>
        </tr>
        {postage != null && showPostage && (
          <tr
            className="c-bag__total c-bag__postage__total"
            tabIndex="0"
            aria-label={`Delivery cost is ${
              discount && postage - discount <= 0
                ? 'free'
                : currency + (postage - discount)
            }`}
          >
            <td className="c-bag__total__postage" colSpan={cols}>
              <div className="c-bag__price-container">
                <FormattedMessage
                  id="bag.delivery"
                  defaultMessage="Delivery:"
                />
                <strong className="c-bag__total__price c-bag__price">
                  {discount && postage - discount <= 0 ? (
                    <span className="c-bag__free-badge">
                      <FormattedMessage
                        id="delivery-methods.free"
                        defaultMessage="Free"
                      />
                    </span>
                  ) : (
                    <FormattedNumber
                      value={postage - discount}
                      currency={currency}
                      style="currency"
                    />
                  )}
                </strong>
              </div>
            </td>
          </tr>
        )}
        {!isEditable && usTaxAmount !== null && showUSTax && (
          <tr
            className="c-bag__total c-bag__grand__total"
            tabIndex="0"
            aria-label={`Tax amount: ${currency}${usTaxAmount}`}
          >
            <td className="c-bag__total__value" colSpan={cols}>
              <div className="c-bag__price-container">
                <FormattedMessage id="bag.tax" defaultMessage="Tax:" />
                <strong className="c-bag__total__tax__price c-bag__tax">
                  <FormattedNumber
                    value={usTaxAmount}
                    currency={currency}
                    style="currency"
                  />
                </strong>
              </div>
            </td>
          </tr>
        )}
        {total != null && showPostage && (
          <tr
            className="c-bag__total c-bag__grand__total"
            tabIndex="0"
            aria-label={`Grand total: ${currency}${
              locale === 'en-US' ? totalUSAmount : total
            }`}
          >
            <td className="c-bag__total__value" colSpan={cols}>
              <div className="c-bag__price-container">
                <FormattedMessage
                  id="bag.grand-total"
                  defaultMessage="Grand total:"
                />
                <strong className="c-bag__total__price c-bag__price">
                  <FormattedNumber
                    value={locale === 'en-US' ? totalUSAmount : total}
                    currency={currency}
                    style="currency"
                  />
                </strong>
              </div>
            </td>
          </tr>
        )}
        {promotionMessages.map(({ message }) => (
          <tr className="c-bag__promotions" key={`row-${message.id}`}>
            <td colSpan={cols} key={`cell-${message.id}`}>
              <PromotionMessage content={message} key={message.id} />
            </td>
          </tr>
        ))}
      </tfoot>
    );

    const getBagItemElement = (item, stockGroup) => {
      const modifiers = [
        // Add the stock group name to the modifiers array
        findKey(LOCATION_STOCK_GROUP_MAP, group => group === stockGroup),
      ];
      const props = { ...bagItemProps, item, modifiers };
      return <BagItem key={item.id + item.code} {...props} />;
    };

    // Bag items can either be:
    // - a multi-dimensional array so they can be grouped by the stockGroup key.
    let nestedBagItems: Element<*>[][];
    // - a flat array of bag items.
    let bagItems: Element<*>[];

    if (splitItemsByDeliveryTimes) {
      nestedBagItems = items.reduce(
        (itemsByStockGroup: Element<*>[][], item: BagItemType) => {
          // Stock group should always exist but if it doesn't for some reason then set it to unknown so it won't be
          // available for upgrades.
          let stockGroup = item.stockGroup || LOCATION_STOCK_GROUP_MAP.unknown;

          // If we are sending the items abroad then group in shop and in warehouse together. Assume everything is in
          // warehouse.
          if (
            !isDomestic &&
            item.stockGroup === LOCATION_STOCK_GROUP_MAP.shop
          ) {
            stockGroup = LOCATION_STOCK_GROUP_MAP.warehouse;
          }

          const bagItem = getBagItemElement(item, stockGroup);
          itemsByStockGroup[stockGroup].push(bagItem);
          return itemsByStockGroup;
        },
        [[], [], [], []]
      );
    } else {
      bagItems = items.map(item => {
        // Stock group should always exist but if it doesn't for some reason then set it to unknown so it won't be
        // available for upgrades.
        const stockGroup = item.stockGroup || LOCATION_STOCK_GROUP_MAP.unknown;
        return getBagItemElement(item, stockGroup);
      });
    }
    const handleScroll = () => {
      forceCheck();
    };

    let amount;

    if (locale === 'en-US') {
      amount = totalUSAmount;
    } else {
      amount = total || subTotal;
    }

    return (
      <>
        {isMobile ? (
          <>
            {this.renderMobileSummary(
              <>
                <Accordion>
                  <Accordion.Item
                    id={'bag-mobile-accordion'}
                    onClick={() =>
                      this.setState({ hasAccordionBeenOpened: true })
                    }
                    label={
                      <>
                        <b>
                          {this.props.items.length}{' '}
                          {this.props.items.length === 1 ? 'item' : 'items'} –{' '}
                        </b>
                        <FormattedNumber
                          value={amount}
                          currency={currency}
                          style="currency"
                        />
                        {!isEditable &&
                          postage > 0 &&
                          formatMessage(messages.includingDelivery)}
                      </>
                    }
                    key={'faq.id'}
                  >
                    <table style={{ width: '100%', display: 'block' }}>
                      <tbody
                        tabIndex="0"
                        onScroll={handleScroll}
                        style={{
                          maxHeight: `${
                            this.heightBagSummary
                              ? `${this.heightBagSummary}px`
                              : '100%'
                          }`,
                        }}
                        className={isPage ? 'c-bag__main' : 'c-bag__summary'}
                      >
                        {splitItemsByDeliveryTimes && nestedBagItems
                          ? [
                              // Get all the items currently located in the warehouse.
                              ...getItemsLocatedIn(
                                'warehouse',
                                nestedBagItems,
                                titleMessages,
                                upSellPremiumPostage,
                                onUpSellPostage
                              ),

                              // Get all the items currently located in a shop.
                              // These items will not be available for premium delivery upgrades.
                              ...getItemsLocatedIn(
                                'shop',
                                nestedBagItems,
                                titleMessages
                              ),

                              // Get all the items that we know are available, but we cannot guarantee when they will be dispatched.
                              ...getItemsLocatedIn(
                                'unknown',
                                nestedBagItems,
                                titleMessages
                              ),
                            ]
                          : bagItems}
                      </tbody>
                      {this.footer}
                    </table>
                  </Accordion.Item>
                </Accordion>
                {isCheckoutFlow &&
                  !this.state.hasAccordionBeenOpened &&
                  checkoutStep === 3 &&
                  items.some(item => item.stockStatus !== 'InStock') && (
                    <>
                      <div className="c-bag__delivery-notification-tooltip" />
                      {deliveryNotification(true)}
                    </>
                  )}
              </>
            )}
          </>
        ) : (
          <div className={`${STYLE_PREFIX}__container`}>
            <table
              aria-label="Items in your shopping bag"
              role="table"
              id={location}
              className={applyModifiers(
                STYLE_PREFIX,
                isEditable && ['is-editable']
              )}
              summary={formatMessage(messages.summary)}
            >
              <thead className="c-bag__header u-hidden">
                <tr>
                  <th scope="column">
                    <FormattedMessage
                      id="bag.product"
                      defaultMessage="Product"
                    />
                  </th>
                  {isEditable && (
                    <th scope="column">
                      <FormattedMessage
                        id="bag.quantity.select"
                        defaultMessage="Quantity - select"
                      />
                    </th>
                  )}

                  {isEditable && (
                    <th scope="column">
                      <FormattedMessage
                        id="bag.remove"
                        defaultMessage="Remove"
                      />
                    </th>
                  )}
                  {!isEditable && (
                    <th scope="column">
                      <FormattedMessage
                        id="bag.quantity"
                        defaultMessage="Quantity"
                      />
                    </th>
                  )}
                  <th scope="column">
                    <FormattedMessage id="bag.price" defaultMessage="Price" />
                  </th>
                </tr>
              </thead>
              <tbody
                tabIndex="0"
                onScroll={handleScroll}
                style={{
                  maxHeight: `${
                    this.heightBagSummary
                      ? `${this.heightBagSummary}px`
                      : '100%'
                  }`,
                }}
                className={isPage ? 'c-bag__main' : 'c-bag__summary'}
              >
                {splitItemsByDeliveryTimes && nestedBagItems
                  ? [
                      // Get all the items currently located in the warehouse.
                      ...getItemsLocatedIn(
                        'warehouse',
                        nestedBagItems,
                        titleMessages,
                        upSellPremiumPostage,
                        onUpSellPostage
                      ),

                      // Get all the items currently located in a shop.
                      // These items will not be available for premium delivery upgrades.
                      ...getItemsLocatedIn(
                        'shop',
                        nestedBagItems,
                        titleMessages
                      ),

                      // Get all the items that we know are available, but we cannot guarantee when they will be dispatched.
                      ...getItemsLocatedIn(
                        'unknown',
                        nestedBagItems,
                        titleMessages
                      ),
                    ]
                  : bagItems}
              </tbody>
              {location !== 'bagPage' && this.footer}
            </table>
            {children}
          </div>
        )}
      </>
    );
  }
}

Bag.propTypes = {
  currency: PropTypes.string,
  delivery: PropTypes.shape({
    postage: PropTypes.number,
    discount: PropTypes.number,
    methodLabel: PropTypes.string,
  }),
  disableClickableThumbnails: PropTypes.bool,
  displaySaveItemButton: PropTypes.bool,
  displaySignInLink: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  isDomestic: PropTypes.bool,
  isPage: PropTypes.bool,
  isEditable: PropTypes.bool,
  isPremiumAvailable: PropTypes.bool,
  isMobile: PropTypes.bool,
  premium: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      code: PropTypes.string,
      price: PropTypes.number,
      quantity: PropTypes.number,
      stockGroup: PropTypes.number,
      stockStatus: PropTypes.string,
    })
  ),
  onDelete: PropTypes.func,
  onChange: PropTypes.func,
  onSaveItemPress: PropTypes.func,
  savedItemsMap: PropTypes.object,
  onUpSellPostage: PropTypes.func,
  payment: PropTypes.shape({
    card: PropTypes.object,
    giftVouchers: PropTypes.arrayOf(
      PropTypes.shape({
        reference: PropTypes.string,
        value: PropTypes.number,
      })
    ),
  }),
  promotionMessages: PropTypes.arrayOf(
    PropTypes.shape({
      displayLocations: PropTypes.arrayOf(PropTypes.string),
      message: PropTypes.object.isRequired,
    })
  ),
  showPostage: PropTypes.bool,
  splitItemsByDeliveryTimes: PropTypes.bool,
  subTotal: PropTypes.number,
  total: PropTypes.number,
  usTaxAmount: PropTypes.number,
  locale: PropTypes.string,
  showUSTax: PropTypes.bool,
  location: PropTypes.string,
  onFooterRender: PropTypes.func,
  children: PropTypes.node,
  showEditButton: PropTypes.bool,
  title: PropTypes.string,
  titleMessages: PropTypes.shape({
    warehouse: PropTypes.node,
    shop: PropTypes.node,
    unknown: PropTypes.node,
  }),
};

export default injectIntl(Bag);
